/**
 * Auto Repair - Work Orders Page (Task 61)
 */

import { useEffect, useState } from 'react';
import { WorkOrderList } from '@/components/industry/shared/WorkOrderList';
import { WorkOrderForm } from '@/components/industry/shared/WorkOrderForm';
import { useIndustryWorkOrders, useIndustryVehicles } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { IndustryWorkOrder } from '@/types/industry';
import { MainLayout } from "@/components/MainLayout";

export default function AutoRepairOrdersPage() {
  useEffect(() => {
    document.title = 'Ordens de Serviço | Controle Financeiro';
  }, []);

  const { workOrders, isLoading, createWorkOrder, updateWorkOrder, deleteWorkOrder } =
    useIndustryWorkOrders({ domain: CompanyType.AUTO_REPAIR });
  const { vehicles } = useIndustryVehicles({ domain: CompanyType.AUTO_REPAIR });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState<IndustryWorkOrder | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format vehicles for the form
  const vehicleOptions = (vehicles || []).map((v) => ({
    id: v.id,
    plate: (v.metadata?.plate as string) || '',
    make: (v.metadata?.make as string) || '',
    model: (v.metadata?.model as string) || '',
  }));

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const vehicle = vehicles?.find((v) => v.id === data.vehicle_id);

      const workOrderData = {
        vehicle_id: data.vehicle_id,
        start_date: data.start_date.toISOString().split('T')[0],
        status: data.status,
        metadata: {
          diagnosis: data.diagnosis,
          workPerformed: data.work_performed,
          laborCost: data.labor_cost,
          partsCost: data.parts_cost,
          notes: data.notes,
          vehiclePlate: vehicle?.metadata?.plate,
        },
      };

      if (editingWorkOrder) {
        await updateWorkOrder({ id: editingWorkOrder.id, data: workOrderData });
        toast.success('Ordem de serviço atualizada');
      } else {
        await createWorkOrder(workOrderData);
        toast.success('Ordem de serviço criada');
      }
      setIsFormOpen(false);
      setEditingWorkOrder(undefined);
    } catch (error) {
      toast.error('Erro ao salvar ordem de serviço');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
<MainLayout>
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </div>
    
    </MainLayout>);
  }

  return (
<MainLayout>
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Ordens de Serviço</h1>
        <p className="text-muted-foreground">Gerencie os serviços realizados</p>
      </div>

      {vehicleOptions.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>Nenhum veículo cadastrado.</p>
          <p className="text-sm mt-2">Cadastre veículos primeiro para criar ordens de serviço.</p>
        </div>
      ) : (
        <>
          <WorkOrderList
            workOrders={workOrders || []}
            onEdit={(workOrder) => {
              setEditingWorkOrder(workOrder);
              setIsFormOpen(true);
            }}
            onDelete={async (workOrder) => {
              if (window.confirm('Excluir ordem de serviço?')) {
                await deleteWorkOrder(workOrder.id);
                toast.success('Ordem de serviço excluída');
              }
            }}
            onCreate={() => {
              setEditingWorkOrder(undefined);
              setIsFormOpen(true);
            }}
          />

          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingWorkOrder ? 'Editar' : 'Nova'} Ordem de Serviço</DialogTitle>
              </DialogHeader>
              <WorkOrderForm
                workOrder={editingWorkOrder}
                vehicles={vehicleOptions}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setIsFormOpen(false);
                  setEditingWorkOrder(undefined);
                }}
                isSubmitting={isSubmitting}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  
    </MainLayout>);
}
